/**
 * Main Application Component
 * მთავარი აპლიკაციის კომპონენტი
 *
 * @version 1.3.0
 * @since 2026-02-02
 */

import { useEffect, useMemo } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout, ProtectedRoute, useAuth } from '@aiia/portal-core';
import { useCurrentUser } from './hooks/useUser';

import { getNavigationItems, getAdminNavigationItems } from './config/navigation';
import LanguageSwitcher from './components/LanguageSwitcher';

// Landing Page (public - no auth required)
import LandingPage from './pages/Landing';

// Public Pages (no auth required)
import AboutPage from './pages/public/About';
import ContactPage from './pages/public/Contact';
import PrivacyPage from './pages/public/Privacy';
import TermsPage from './pages/public/Terms';
import CookiesPage from './pages/public/Cookies';

// Pages (protected)
import DashboardPage from './pages/Dashboard';
import ApiKeysPage from './pages/ApiKeys';
import WidgetsPage from './pages/Widgets';
import WidgetEditorPage from './pages/WidgetEditor';
import DataFilesPage from './pages/DataFiles';
import ToolsPage from './pages/Tools';
import ToolExecutionsPage from './pages/ToolExecutions';
import PromptsPage from './pages/Prompts';
import SettingsPage from './pages/Settings';
import SubscriptionPage from './pages/Subscription';
import AnalyticsPage from './pages/Analytics';
import ExamplesPage from './pages/Examples';

// Admin Pages
import GeminiKeysPage from './pages/admin/GeminiKeys';
import StatisticsPage from './pages/admin/Statistics';
import UsersPage from './pages/admin/Users';
import SubscriptionsPage from './pages/admin/Subscriptions';
import PlansPage from './pages/admin/Plans';
import AllWidgetsPage from './pages/admin/AllWidgets';
import AllApiKeysPage from './pages/admin/AllApiKeys';
import AllDataFilesPage from './pages/admin/AllDataFiles';
import InvoicesPage from './pages/admin/Invoices';

import { LogoIcon } from './components/Logo';
import HelpPanel from './components/HelpPanel';
import WelcomeModal from './components/WelcomeModal';

const SUPPORTED_LANGS = ['en', 'ka'];

/**
 * Portal App - Authenticated routes
 */
function PortalApp() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { user: portalUser } = useCurrentUser();
  const { lang } = useParams<{ lang: string }>();

  // Determine active language: URL param > current i18n
  const activeLang = lang && SUPPORTED_LANGS.includes(lang) ? lang : i18n.language || 'en';

  // Sync i18n from URL language param
  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Sync i18n language from user profile on first load (only when no URL lang)
  useEffect(() => {
    if (!lang && portalUser?.language && portalUser.language !== i18n.language) {
      i18n.changeLanguage(portalUser.language);
    }
  }, [portalUser?.language, lang, i18n]);

  // Check if user has platform_admin role
  const isPlatformAdmin = useMemo(() => {
    if (!user) return false;
    const roles = user.roles ?? [];
    return roles.includes('platform_admin') || roles.includes('PLATFORM_ADMIN') || roles.includes('ROLE_PLATFORM_ADMIN');
  }, [user]);

  // Combine navigation items based on user role — paths include /{lang}/ prefix
  const navigationItems = useMemo(() => getNavigationItems(t, activeLang), [t, activeLang]);
  const adminNavigationItems = useMemo(() => getAdminNavigationItems(t, activeLang), [t, activeLang]);

  const sidebarItems = useMemo(() => {
    if (isPlatformAdmin) {
      return [
        ...navigationItems,
        { id: 'admin-section', label: t('nav.admin') },
        ...adminNavigationItems,
      ];
    }
    return navigationItems;
  }, [isPlatformAdmin, navigationItems, adminNavigationItems, t]);

  return (
    <ProtectedRoute useKeycloakLogin={false} redirectTo={`/${activeLang}`}>
      <>
      <Layout
        sidebarItems={sidebarItems}
        logo={<LogoIcon size={90} />}
        logoCollapsed={<LogoIcon size={81} />}
        user={user}
        onLogout={logout}
        headerContent={<div className="flex justify-end"><LanguageSwitcher /></div>}
        sidebarFooter={
          <span className="text-xs text-gray-500">v{__APP_VERSION__}</span>
        }
      >
        <Routes>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="api-keys" element={<ApiKeysPage />} />
          <Route path="widgets" element={<WidgetsPage />} />
          <Route path="widgets/new" element={<WidgetEditorPage />} />
          <Route path="widgets/:id/edit" element={<WidgetEditorPage />} />
          <Route path="data-files" element={<DataFilesPage />} />
          <Route path="tools" element={<ToolsPage />} />
          <Route path="tool-executions" element={<ToolExecutionsPage />} />
          <Route path="prompts" element={<PromptsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="examples" element={<ExamplesPage />} />
          {/* Admin routes */}
          {isPlatformAdmin && (
            <>
              <Route path="admin/statistics" element={<StatisticsPage />} />
              <Route path="admin/users" element={<UsersPage />} />
              <Route path="admin/subscriptions" element={<SubscriptionsPage />} />
              <Route path="admin/plans" element={<PlansPage />} />
              <Route path="admin/widgets" element={<AllWidgetsPage />} />
              <Route path="admin/api-keys" element={<AllApiKeysPage />} />
              <Route path="admin/data-files" element={<AllDataFilesPage />} />
              <Route path="admin/invoices" element={<InvoicesPage />} />
              <Route path="admin/gemini-keys" element={<GeminiKeysPage />} />
            </>
          )}
        </Routes>
      </Layout>
      <HelpPanel />
      <WelcomeModal />
      </>
    </ProtectedRoute>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Public Pages with language prefix */}
      <Route path="/:lang" element={<LandingPage />} />
      <Route path="/:lang/about" element={<AboutPage />} />
      <Route path="/:lang/contact" element={<ContactPage />} />
      <Route path="/:lang/privacy" element={<PrivacyPage />} />
      <Route path="/:lang/terms" element={<TermsPage />} />
      <Route path="/:lang/cookies" element={<CookiesPage />} />

      {/* Protected Portal Routes with language prefix */}
      <Route path="/:lang/*" element={<PortalApp />} />

      {/* Protected Portal Routes without language (loads English by default) */}
      <Route path="/*" element={<PortalApp />} />
    </Routes>
  );
}

export default App;
